/**
 * AuthStatusAnnouncer - Screen reader announcements for auth status changes
 * Provides accessible feedback for authentication state changes
 */

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAuthStore } from '@stores/authStore';

// ============================================================================
// Styled Components
// ============================================================================

const LiveRegion = styled.div`
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

// ============================================================================
// Component
// ============================================================================

export const AuthStatusAnnouncer: React.FC = () => {
  const { error, isLoading, isAuthenticated } = useAuthStore();
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    if (error) {
      setAnnouncement(`Error de autenticación: ${error}`);
    } else if (isLoading) {
      setAnnouncement('Verificando autenticación...');
    } else if (isAuthenticated) {
      setAnnouncement('Autenticación exitosa');
    }
  }, [error, isLoading, isAuthenticated]);

  // Clear announcement after a delay to avoid repetitive announcements
  useEffect(() => {
    if (announcement) {
      const timer = setTimeout(() => {
        setAnnouncement('');
      }, 1000);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [announcement]);

  return (
    <LiveRegion aria-live="polite" aria-atomic="true" role="status">
      {announcement}
    </LiveRegion>
  );
};

export default AuthStatusAnnouncer;
